// MIA QR Payment Page (Moldova)
import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Plus, Info, RefreshCw, CheckCircle, Clock } from "lucide-react";
import { SBER, API, pageVariants, tapAnimation } from "../utils/constants";
import { SberCard } from "../components/UIComponents";

const MIAPaymentPage = ({ user, onBack, onSuccess, t }) => {
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [checkingStatus, setCheckingStatus] = useState(false);
  
  const balanceMdl = user?.balance_mdl || 0;

  const handleCreateQR = async () => {
    const amountNum = parseFloat(amount);
    if (!amountNum || amountNum <= 0) {
      toast.error(t ? t('topup.enterAmount') : 'Введите сумму');
      return;
    }
    if (amountNum < 10) {
      toast.error(`${t ? t('topup.min') : 'Минимум'} 10 MDL`);
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/mia/create-qr`, {
        amount: amountNum,
        description: `Пополнение ${amountNum} MDL`,
        telegram_id: user?.telegram_id,
        user_id: user?.user_id,
        ttl: 600
      });

      if (res.data.success) {
        setQrData(res.data);
        setPaymentStatus('PENDING');
        toast.success(t ? t('actions.copied') : 'QR код создан');
        startStatusPolling(res.data.order_id);
      } else {
        toast.error(res.data.error || (t ? t('errors.somethingWrong') : 'Ошибка создания QR'));
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || (t ? t('errors.somethingWrong') : 'Ошибка создания QR'));
    }
    setLoading(false);
  };

  const startStatusPolling = (orderId) => {
    const checkStatus = async () => {
      try {
        const res = await axios.get(`${API}/mia/payment/${orderId}`);
        const status = res.data.status;
        setPaymentStatus(status);
        
        if (status === 'COMPLETED') {
          toast.success(t ? t('payments.paymentSuccess') : 'Баланс MDL пополнен!');
          if (onSuccess) onSuccess();
          return true;
        } else if (status === 'CANCELLED' || status === 'EXPIRED' || status === 'FAILED') {
          toast.error(status === 'EXPIRED' ? 'Платёж истёк' : 'Платёж отменён');
          return true;
        }
        return false;
      } catch (err) {
        return false;
      }
    };

    let attempts = 0;
    const maxAttempts = 200;
    const interval = setInterval(async () => {
      attempts++;
      const done = await checkStatus();
      if (done || attempts >= maxAttempts) {
        clearInterval(interval);
      }
    }, 3000);
  };

  const handleCheckStatus = async () => {
    if (!qrData?.order_id) return;
    
    setCheckingStatus(true);
    try {
      const res = await axios.get(`${API}/mia/payment/${qrData.order_id}`);
      setPaymentStatus(res.data.status);
      if (res.data.status === 'COMPLETED') {
        toast.success(t ? t('payments.paymentSuccess') : 'Баланс пополнен!');
        if (onSuccess) onSuccess();
      }
    } catch (err) {
      toast.error(t ? t('errors.somethingWrong') : 'Ошибка проверки статуса');
    }
    setCheckingStatus(false);
  };

  const handleCancelQR = async () => {
    if (!qrData?.qr_header_uuid) return;
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/mia/cancel-qr`, {
        qr_header_uuid: qrData.qr_header_uuid
      });
      
      if (res.data.success) {
        toast.info(t ? t('actions.cancel') : 'QR код отменён');
        setQrData(null);
        setPaymentStatus(null);
      } else {
        toast.error(res.data.error || (t ? t('errors.somethingWrong') : 'Ошибка отмены'));
      }
    } catch (err) {
      toast.error(t ? t('errors.somethingWrong') : 'Ошибка отмены QR');
    }
    setLoading(false);
  };

  const handleNewPayment = () => {
    setQrData(null);
    setPaymentStatus(null);
    setAmount('');
  };

  // QR Code display view
  if (qrData) {
    return (
      <motion.div 
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        className="min-h-screen pb-6"
        style={{ background: SBER.bg }}
        data-testid="mia-qr-view"
      >
        <div className="px-5 pt-5 pb-4 flex items-center gap-3" style={{ background: SBER.white }}>
          <motion.button 
            whileTap={tapAnimation}
            onClick={onBack}
            className="w-10 h-10 rounded-full flex items-center justify-center"
            style={{ background: SBER.grayPale }}
          >
            <ArrowLeft className="w-5 h-5" style={{ color: SBER.gray }} />
          </motion.button>
          <h1 className="text-[18px] font-semibold" style={{ color: SBER.dark }}>
            {t ? t('mia.title') : 'Пополнение MDL'}
          </h1>
        </div>

        <div className="px-5 pt-5">
          <SberCard className="p-6 text-center mb-4">
            <div className="mb-4">
              <p className="text-[13px]" style={{ color: SBER.gray }}>{t ? t('withdraw.amount') : 'Сумма пополнения'}</p>
              <p className="text-[28px] font-bold" style={{ color: '#1E40AF' }}>
                {parseFloat(amount).toFixed(2)} MDL
              </p>
            </div>

            <div className="bg-white p-4 rounded-xl inline-block mb-4">
              <img 
                src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrData.qr_link)}`}
                alt="MIA QR Code"
                className="w-48 h-48"
              />
            </div>

            <p className="text-xs mb-2" style={{ color: SBER.grayLight }}>
              {t ? t('mia.scanQR') : 'Отсканируйте QR код в приложении банка'}
            </p>

            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium ${
              paymentStatus === 'COMPLETED' ? 'bg-green-100 text-green-700' :
              paymentStatus === 'PENDING' ? 'bg-yellow-100 text-yellow-700' :
              'bg-red-100 text-red-700'
            }`}>
              {paymentStatus === 'COMPLETED' && <CheckCircle className="w-4 h-4" />}
              {paymentStatus === 'PENDING' && <Clock className="w-4 h-4 animate-spin" />}
              {paymentStatus === 'COMPLETED' ? (t ? t('payments.paymentSuccess') : 'Баланс пополнен!') :
               paymentStatus === 'PENDING' ? (t ? t('general.loading') : 'Ожидание оплаты...') :
               paymentStatus === 'EXPIRED' ? 'Истёк' : 'Отменён'}
            </div>
          </SberCard>

          {paymentStatus === 'PENDING' && (
            <div className="space-y-3">
              <motion.button
                whileTap={tapAnimation}
                onClick={handleCheckStatus}
                disabled={checkingStatus}
                className="w-full py-3 rounded-xl font-semibold flex items-center justify-center gap-2"
                style={{ background: SBER.greenPale, color: SBER.green }}
              >
                {checkingStatus ? <RefreshCw className="w-5 h-5 animate-spin" /> : (
                  <>
                    <RefreshCw className="w-5 h-5" />
                    {t ? t('actions.confirm') : 'Проверить статус'}
                  </>
                )}
              </motion.button>

              <motion.button
                whileTap={tapAnimation}
                onClick={handleCancelQR}
                disabled={loading}
                className="w-full py-3 rounded-xl font-semibold"
                style={{ background: '#FEE2E2', color: '#DC2626' }}
              >
                {t ? t('actions.cancel') : 'Отменить QR'}
              </motion.button>
            </div>
          )}

          {(paymentStatus === 'COMPLETED' || paymentStatus === 'CANCELLED' || paymentStatus === 'EXPIRED') && (
            <motion.button
              whileTap={tapAnimation}
              onClick={paymentStatus === 'COMPLETED' ? onBack : handleNewPayment}
              className="w-full py-4 rounded-xl font-semibold text-white"
              style={{ background: SBER.green }}
            >
              {paymentStatus === 'COMPLETED' ? (t ? t('actions.close') : 'Готово') : (t ? t('actions.continue') : 'Новое пополнение')}
            </motion.button>
          )}

          <div className="mt-4 p-4 rounded-xl" style={{ background: '#DBEAFE' }}>
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium">{t ? t('mia.description') : 'Пополнение через MIA'}</p>
                <p className="mt-1">QR код можно оплатить любым банковским приложением Молдовы.</p>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  // Create QR form view
  return (
    <motion.div 
      variants={pageVariants}
      initial="initial"
      animate="animate"
      exit="exit"
      className="min-h-screen pb-6"
      style={{ background: SBER.bg }}
      data-testid="mia-payment-page"
    >
      <div className="px-5 pt-5 pb-4 flex items-center gap-3" style={{ background: SBER.white }}>
        <motion.button 
          whileTap={tapAnimation}
          onClick={onBack}
          className="w-10 h-10 rounded-full flex items-center justify-center"
          style={{ background: SBER.grayPale }}
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" style={{ color: SBER.gray }} />
        </motion.button>
        <h1 className="text-[18px] font-semibold" style={{ color: SBER.dark }}>
          {t ? t('mia.title') : 'Пополнение MDL'}
        </h1>
      </div>

      <div className="px-5 pt-5">
        <div className="text-center mb-6">
          <div 
            className="w-16 h-16 mx-auto rounded-2xl flex items-center justify-center mb-3"
            style={{ background: 'linear-gradient(135deg, #1e40af 0%, #3b82f6 100%)' }}
          >
            <span className="text-white text-xl font-bold">MDL</span>
          </div>
          <p className="text-sm" style={{ color: SBER.gray }}>
            {t ? t('home.currencies') : 'Текущий баланс'}
          </p>
          <p className="text-[24px] font-bold mt-1" style={{ color: SBER.dark }}>
            {balanceMdl.toFixed(2)} MDL
          </p>
        </div>

        <SberCard className="p-5 mb-4">
          <label className="text-[13px] font-medium mb-3 block" style={{ color: '#1E40AF' }}>
            {t ? t('topup.enterAmount') : 'Сумма пополнения'}
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0"
              className="flex-1 text-[28px] font-bold bg-transparent outline-none"
              style={{ color: SBER.dark }}
              data-testid="mia-amount"
            />
            <span className="text-[18px] font-medium" style={{ color: SBER.gray }}>MDL</span>
          </div>
          
          <div className="flex gap-2 mt-4">
            {[50, 100, 200, 500].map((amt) => (
              <motion.button
                key={amt}
                whileTap={tapAnimation}
                onClick={() => setAmount(String(amt))}
                className="flex-1 py-2.5 rounded-lg text-sm font-semibold"
                style={{ 
                  background: amount === String(amt) ? '#1E40AF' : '#DBEAFE',
                  color: amount === String(amt) ? '#fff' : '#1E40AF'
                }}
              >
                {amt}
              </motion.button>
            ))}
          </div>
        </SberCard>

        <div className="p-4 rounded-xl mb-4" style={{ background: '#DBEAFE' }}>
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">{t ? t('landing.howItWorks') : 'Как пополнить?'}</p>
              <p className="mt-1">1. Введите сумму и нажмите "Создать QR"</p>
              <p>2. Отсканируйте QR в банковском приложении</p>
              <p>3. MDL будут зачислены мгновенно</p>
            </div>
          </div>
        </div>

        <motion.button
          whileTap={tapAnimation}
          onClick={handleCreateQR}
          disabled={loading || !amount || parseFloat(amount) < 10}
          className="w-full py-4 rounded-xl font-semibold text-white flex items-center justify-center gap-2 disabled:opacity-50"
          style={{ background: '#1E40AF' }}
          data-testid="create-mia-qr"
        >
          {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : (
            <>
              <Plus className="w-5 h-5" />
              {t ? t('actions.continue') : 'Создать QR для пополнения'}
            </>
          )}
        </motion.button>
        
        <p className="text-center text-[12px] mt-3" style={{ color: SBER.grayLight }}>
          {t ? t('topup.min') : 'Минимум'} 10 MDL • QR код действителен 10 минут
        </p>
      </div>
    </motion.div>
  );
};

export default MIAPaymentPage;
